import * as Yup from 'yup';
import {
  pipelineSyncFrequencyValidationSchema,
  pipelineSyncFrequencyWithCronValidationSchema
} from '../../../shared/constants/pipeline';
import { PipelineSettingsFormFields } from '../interfaces/pipeline-settings-form-state';
import { Integration } from '../../../shared/interfaces/integration';
import {
  PipelineSettingsFeatures,
  PipelineSettingsFeaturesData
} from '../../interfaces/pipeline-settings-config';
import {
  destinationPrefixValidator,
  partitionKeyValidator,
  pipelineNameValidator
} from '../validators';
import { useAsyncFieldTest } from '../../../shared/components/AsyncValidationForm/hooks/useAsyncFieldTest';

export function usePipelineSettingsValidations(
  pipeline: Integration,
  features: PipelineSettingsFeaturesData
) {
  const destinationPrefixTest = useAsyncFieldTest({
    fieldName: PipelineSettingsFormFields.DESTINATION_PREFIX,
    storedValue: pipeline.destinationPrefix as string,
    isRequired: true,
    requiredMessage: 'Destination prefix is a required field',
    validator: async (value, signal, extraParams) => {
      return destinationPrefixValidator(
        {
          destinationPrefix: value,
          destinationType: pipeline.destination!.destinationType.destinationType
        },
        signal,
        extraParams
      );
    },
    defaultValidationError: 'Destination prefix is invalid',
    extraPrams: {
      integration_id: pipeline.id
    }
  });

  const pipelineNameTest = useAsyncFieldTest({
    fieldName: PipelineSettingsFormFields.PIPELINE_NAME,
    storedValue: pipeline.name,
    isRequired: true,
    requiredMessage: 'Pipeline name is a required field',
    validator: pipelineNameValidator,
    extraPrams: {
      integration_id: pipeline.id
    }
  });

  const partitionKeyTest = useAsyncFieldTest({
    fieldName: PipelineSettingsFormFields.DESTINATION_PARTITION_KEY,
    skipValidationOnStoredValue: true,
    storedValue: pipeline.config.partition_key,
    isRequired: true,
    requiredMessage: 'Destination partition key is a required field',
    validator: async (value, signal) => {
      return partitionKeyValidator(
        {
          destinationPartitionKey: value
        },
        signal
      );
    },
    defaultValidationError: 'Destination partition key is invalid'
  });

  return Yup.object({
    [PipelineSettingsFormFields.DESTINATION_PREFIX]: Yup.string()
      .trim()
      .test('DestinationPrefixAsyncValidation', '', destinationPrefixTest),
    [PipelineSettingsFormFields.PIPELINE_NAME]: Yup.string()
      .trim()
      .test('PipelineNameAsyncValidation', '', pipelineNameTest),
    ...(features.features[PipelineSettingsFeatures.SYNC_FREQUENCY]
      ? {
          [PipelineSettingsFormFields.SYNC_FREQUENCY]: pipelineSyncFrequencyValidationSchema(
            features.features[PipelineSettingsFeatures.SYNC_FREQUENCY]
          )
        }
      : {}),
    ...(features.features[PipelineSettingsFeatures.SYNC_FREQUENCY_IN_CRON]
      ? {
          [PipelineSettingsFormFields.SYNC_FREQUENCY_IN_CRON]:
            pipelineSyncFrequencyWithCronValidationSchema(
              features.features[PipelineSettingsFeatures.SYNC_FREQUENCY_IN_CRON]
            )
        }
      : {}),
    ...(features.features[PipelineSettingsFeatures.SYNC_FREQUENCY_TYPE]
      ? {
          [PipelineSettingsFormFields.SYNC_FREQUENCY_TYPE]: Yup.object().required()
        }
      : {}),
    ...(features.features[PipelineSettingsFeatures.PARTITION_KEY]
      ? {
          [PipelineSettingsFormFields.DESTINATION_PARTITION_KEY]: Yup.string()
            .trim()
            .test('PartitionKeyAsyncValidation', '', partitionKeyTest)
        }
      : {}),
    [PipelineSettingsFormFields.LATENCY_THRESHOLD_ALERT_ENABLED]: Yup.boolean(),
    [PipelineSettingsFormFields.LATENCY_THRESHOLD]: Yup.number().when(
      [PipelineSettingsFormFields.LATENCY_THRESHOLD_ALERT_ENABLED],
      (latencyEnabled, schema) => {
        if (!latencyEnabled) {
          return schema.nullable().notRequired();
        }

        const isSyncFrequencyEnabled =
          !!features.features[PipelineSettingsFeatures.SYNC_FREQUENCY] ||
          !!features.features[PipelineSettingsFeatures.SYNC_FREQUENCY_IN_CRON];

        return isSyncFrequencyEnabled
          ? schema
              .required()
              .min(5, `Latency threshold must be >= 5 minutes`)
              .max(15 * 24 * 60, 'Latency threshold must be less than 15 days')
          : schema.required();
      }
    )
  });
}
